// loop/hookLifecycle.js — E8 (super-marathon): fire lifecycle hooks in the agent loop.
//
// The LIVE hook system is hooksRunner.js (fireHooks → /api/code-ide/hooks → the Electron
// executor in electron/hooks.cjs). It already fires `user_prompt_submit` from the composer;
// the `pre_tool_call` / `post_tool_call` / `stop` events were authored (UI + templates)
// but marked PENDING because nothing dispatched them in the loop. This module is that
// dispatch, wired at the tool/turn boundaries of the spine.
//
// Gated by apolo_hooks_lifecycle (DEFAULT OFF): with the flag off every function returns
// immediately WITHOUT calling fireHooks — no network/IPC, byte-identical to today. Impure
// by design (fireHooks touches window/IPC/network) — this is NOT a pure-engine, so it is
// wired here (loop/) rather than added inline to the dp-loc-capped spine.
//
// SACRED: hooks can only ADD a deny (block) — never liberate a tool. The real command
// still passes through the tool gate + commandSafety AFTER this (defense-in-depth).

import { fireHooks } from '../../hooksRunner.js';
import { readHooksEnabled } from '../../flags/sprintFlags.js';

const safeJson = (v) => { try { return JSON.stringify(v); } catch { return ''; } };
const clip = (v) => String(v ?? '').slice(0, 4000);

/**
 * pre_tool_call gate: run matching pre-tool hooks; if a BLOCKING hook exits non-zero,
 * return a gate-shaped deny (fed into evaluateToolGate). Returns null when the flag is
 * off, no bridge, or nothing blocked — the tool proceeds exactly as today.
 * @param {string} toolName
 * @param {object} args
 * @returns {Promise<{ denied: true, reason: string } | null>}
 */
export async function preToolHookGate(toolName, args) {
  if (!readHooksEnabled()) return null;
  const r = await fireHooks('pre_tool_call', { tool: toolName, tool_input_json: safeJson(args) }).catch(() => null);
  if (r?.blocked) {
    return { denied: true, reason: `hook pre_tool_call bloqueou ${toolName}: ${r.blockingStderr || r.blockingHook || 'saída ≠ 0'}` };
  }
  return null;
}

/**
 * post_tool_call observe: fire matching post-tool hooks (best-effort, never throws into
 * the loop). No-op when the flag is off.
 * @param {string} toolName
 * @param {object} args
 * @param {string} result
 */
export async function postToolHook(toolName, args, result) {
  if (!readHooksEnabled()) return;
  await fireHooks('post_tool_call', { tool: toolName, tool_input_json: safeJson(args), tool_result: clip(result) }).catch(() => {});
}

/**
 * stop observe: fire matching `stop` hooks when the turn completes (best-effort).
 * No-op when the flag is off.
 * @param {string} finalText
 */
export async function stopHook(finalText) {
  if (!readHooksEnabled()) return;
  await fireHooks('stop', { tool: 'stop', final_text: clip(finalText) }).catch(() => {});
}
